import React from 'react';
import { formatSize, toFlag, toPercent } from 'pano-utils';
import {
  type CFAudio,
  type CFMedia,
  FeatureType,
  LibType,
  MediaType,
} from '@types';
import { Collapse } from '@components/Collapse';
import Tip from '@components/Tip';
import Button from '@components/Button';
import AudioPlayer from '@components/AudioPlayer';
import Slider from '@components/Slider';
import FeatureToggle from '@components/FeatureToggle';
import { useCustomFeature } from '../../CustomFeatureContext';
import { LIB_SIZE_LIMIT, selectLibAudios } from '../../../mediaLib';
import { LayoutSetting } from '../../components/LayoutSetting';

const sizeLimit = formatSize(LIB_SIZE_LIMIT[LibType.audio]);

interface Props {
  isRequired?: boolean;
}

function CAudioSetting({ isRequired = false }: Props) {
  const { data, setData } = useCustomFeature<CFMedia | CFAudio>();
  const {
    code,
    type = MediaType.lib,
    musicMediaUrl,
    musicMediaName,
    musicVolume,
    musicLoop,
    turnOffBgMusicOnPlay,
    turnOffRotateOnPlay,
  } = data;

  const handleSelectAudio = async () => {
    const { libItems, sysItems } = await selectLibAudios();
    if (libItems) {
      const item = libItems[0];
      setData((draft) => {
        draft.type = MediaType.lib;
        draft.musicMediaId = item.id;
        draft.musicMediaUrl = item.url;
        draft.musicMediaName = item.name;
      });
    } else if (sysItems) {
      const item = sysItems[0];
      setData((draft) => {
        draft.type = MediaType.system;
        draft.musicMediaId = item.id;
        draft.musicMediaUrl = item.url;
        draft.musicMediaName = item.name;
      });
    }
  };

  return (
    <>
      <Collapse title="音频设置">
        <AudioPlayer
          mediaType={type}
          src={musicMediaUrl}
          name={musicMediaName}
          volume={musicVolume}
          isRequired={isRequired}
          onDelete={() =>
            setData((draft) => {
              draft.musicMediaId = 0;
              draft.musicMediaName = '';
              draft.musicMediaUrl = '';
              draft.musicVolume = 1;
            })
          }
        />
        <div className="mt-10 flex items-center justify-between">
          <Tip>{`文件体积不超过${sizeLimit}`}</Tip>
          <Button size="sm" onClick={handleSelectAudio}>
            选择音频
          </Button>
        </div>
        {!!musicMediaUrl && (
          <>
            <Slider
              min={0}
              max={1}
              value={musicVolume}
              className="mt-20"
              valToString={(val) => toPercent(val, 0)}
              onChange={(val) =>
                setData((draft) => {
                  draft.musicVolume = val;
                })
              }
            >
              <Slider.Header
                title={
                  <div>
                    <span>音量：</span>
                    <Tip as="span">仅PC和安卓有效</Tip>
                  </div>
                }
              />
              <Slider.Bar step={0.01} />
            </Slider>
            <FeatureToggle
              title="循环播放"
              isOn={!!musicLoop}
              onToggle={(isOn) => {
                setData((draft) => {
                  draft.musicLoop = toFlag(isOn);
                });
              }}
            />
          </>
        )}
      </Collapse>
      <Collapse title="播放控制">
        <FeatureToggle
          title="播放时关闭背景音乐"
          isOn={!!turnOffBgMusicOnPlay}
          onToggle={(isOn) => {
            setData((draft) => {
              draft.turnOffBgMusicOnPlay = toFlag(isOn);
            });
          }}
        />
        <FeatureToggle
          title="播放时关闭导览讲解"
          isOn={!!(turnOffRotateOnPlay ?? 0)}
          onToggle={(isOn) => {
            setData((draft) => {
              (draft as CFMedia).turnOffRotateOnPlay = toFlag(isOn);
            });
          }}
        />
        {code === FeatureType.media && (
          <>
            <div className="mt-20 mb-10 flex">排版方式</div>
            <LayoutSetting
              value={data.layoutStyle ?? 0}
              withSplitOption
              onChange={(val) => {
                setData((draft) => {
                  (draft as CFMedia).layoutStyle = val;
                });
              }}
            />
          </>
        )}
      </Collapse>
    </>
  );
}

export default CAudioSetting;
